import { MODAL_PRIORITIES } from 'helpers/LayoutHelpers';
import { createQueue, createStack } from 'helpers/ObjectHelpers';

import { createV2 } from './StoreCreator';

import type { IStoreAction, IStoreActions } from './StoreCreator';
import type { Queue, Stack } from 'helpers/ObjectHelpers';

export interface IModalListItem {
  id: string,
  depth?: number,
  priority?: number,
  immediate?: boolean,
  props?: any
}

interface ModalStoreState {
  stack: Stack<IModalListItem>,
  queue: Queue<IModalListItem>
}

const initialState: ModalStoreState = {
  stack: createStack<IModalListItem>(),
  queue: createQueue<IModalListItem>(),
};

const reducers = {

  addToStack: (draft: ModalStoreState, action: IStoreAction<IModalListItem>) => {
    const { payload } = action;
    const { stack } = draft;

    if (!stack.contains(payload.id)) {
      stack.push(payload);
    }

    return {
      ...draft,
      stack
    };
  },

  removeFromStack: (draft: ModalStoreState, action: IStoreAction<IModalListItem['id']>) => {
    const { payload } = action;
    const { stack } = draft;

    stack.remove(payload);

    return {
      ...draft,
      stack
    };
  },

  addToQueue: (draft: ModalStoreState, action: IStoreAction<IModalListItem>) => {
    const { payload } = action;
    const { stack, queue } = draft;
    const { id, priority: customPriority } = payload || {};

    const defaultPriority = MODAL_PRIORITIES.indexOf(id as any) * 10;
    const priority = customPriority !== undefined ? customPriority : defaultPriority;

    if (stack.isEmpty()) {
      stack.push({ ...payload, priority });
    } else {
      queue.enqueue({ ...payload, priority });
    }

    return {
      ...draft,
      stack,
      queue
    };
  },

  removeFromQueue: (draft: ModalStoreState, action: IStoreAction<IModalListItem['id']>) => {
    const { payload } = action;
    const { queue } = draft;

    queue.remove(payload);

    return {
      ...draft,
      queue
    };
  },

  moveFromQueueToStack: (draft: ModalStoreState, action: IStoreAction<null>) => {
    const { stack, queue } = draft;
    const pendingModal = queue.dequeue();

    if (pendingModal) {
      stack.push(pendingModal);
    }

    return {
      ...draft,
      stack,
      queue
    };
  }

};

type Actions = IStoreActions<typeof reducers>;

const [
  ModalProvider,
  useModalStore,
  useDispatch,
  actions
] = createV2<ModalStoreState, Actions>(reducers, initialState);

function useModalDispatch() {
  const dispatch = useDispatch();

  const addToStack = (props: IModalListItem) => {
    dispatch(actions.addToStack(props));
  };

  const removeFromStack = (props: IModalListItem['id']) => {
    dispatch(actions.removeFromStack(props));
  };

  const addToQueue = (props: IModalListItem) => {
    dispatch(actions.addToQueue(props));
  };

  const removeFromQueue = (props: IModalListItem['id']) => {
    dispatch(actions.removeFromQueue(props));
  };

  const moveFromQueueToStack = () => {
    dispatch(actions.moveFromQueueToStack(null));
  };

  return {
    // stack
    addModalToStack: addToStack,
    removeModalFromStack: removeFromStack,

    // queue
    addModalToQueue: addToQueue,
    removeModalFromQueue: removeFromQueue,
    moveModalFromQueueToStack: moveFromQueueToStack
  };
}

// Export

export {
  ModalProvider,
  useModalStore,
  useModalDispatch,
};
